// src/components/messaging/MessagingCenter.js - UPDATED VERSION
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { messagingApi } from '../../api/messaging';
import {
  FaEnvelope,
  FaEnvelopeOpen,
  FaPaperclip,
  FaExclamationTriangle,
  FaClock,
  FaUser,
  FaFileAlt,
  FaCreditCard,
  FaSearch,
  FaCaretDown,
  FaCaretUp,
  FaReply,
  FaTrash,
  FaEye,
  FaTag,
  FaComments,
  FaBell,
  FaPlus,
  FaDownload,
  FaTimes,
  FaSpinner,
  FaCheck,
  FaPrint,
  FaSync,
  FaBox,
  FaReceipt,
} from 'react-icons/fa';
import './MessagingCenter.css';

const MessagingCenter = () => {
  const { user, token } = useContext(AuthContext);
  
  // Main states
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [stats, setStats] = useState(null);
  
  // User data states
  const [userOrders, setUserOrders] = useState([]);
  const [userPayments, setUserPayments] = useState([]);
  const [loadingUserData, setLoadingUserData] = useState(false);
  
  // Filters and sorting
  const [filters, setFilters] = useState({
    message_type: '',
    is_read: '',
    complaint_type: '',
    search: ''
  });
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedMessages, setSelectedMessages] = useState([]);
  
  // Modal states
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showComplaintModal, setShowComplaintModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  
  // Current message
  const [currentMessage, setCurrentMessage] = useState(null);
  
  // Form states
  const [newMessageForm, setNewMessageForm] = useState({
    messageType: 'CLIENT_MESSAGE',
    recipientId: '',
    orderId: '',
    paymentId: '',
    subject: '',
    content: '',
    complaintType: '',
    attachment: null
  });
  
  const [complaintForm, setComplaintForm] = useState({
    complaintType: '',
    orderId: '',
    transactionId: '',
    subject: '',
    content: '',
    attachment: null,
  });
  
  const [replyForm, setReplyForm] = useState({
    content: '',
    attachment: null
  });
  
  const [sending, setSending] = useState(false);

  // Message type labels
  const messageTypeLabels = {
    'ORDER_UPDATE': { label: 'Order Update', color: 'blue', icon: <FaFileAlt /> },
    'PAYMENT_UPDATE': { label: 'Payment Update', color: 'green', icon: <FaCreditCard /> },
    'COMPLAINT': { label: 'Complaint', color: 'red', icon: <FaExclamationTriangle /> },
    'ADMIN_MESSAGE': { label: 'Admin Message', color: 'purple', icon: <FaUser /> },
    'CLIENT_MESSAGE': { label: 'Message to Admin', color: 'orange', icon: <FaUser /> },
    'WRITER_MESSAGE': { label: 'Writer Message', color: 'teal', icon: <FaUser /> },
    'SUPPORT_TICKET': { label: 'Support Ticket', color: 'indigo', icon: <FaComments /> },
    'SYSTEM_NOTIFICATION': { label: 'System Notification', color: 'gray', icon: <FaBell /> }
  };
  
  const complaintTypeLabels = {
    'ORDER_QUALITY': 'Order Quality',
    'PAYMENT_ISSUE': 'Payment Issue',
    'DELIVERY_TIME': 'Delivery Time',
    'WRITER_PERFORMANCE': 'Writer Performance',
    'GENERAL': 'General',
    'TECHNICAL': 'Technical',
    'OTHER': 'Other'
  };
  
  const complaintStatusLabels = {
    'OPEN': 'Open',
    'IN_PROGRESS': 'In Progress',
    'RESOLVED': 'Resolved',
    'CLOSED': 'Closed'
  };

  // ========== SCROLL TO TOP FOR MODALS ==========
  useEffect(() => {
    const handleModalOpen = () => {
      if (showMessageModal || showComplaintModal || showViewModal || showReplyModal) {
        // Scroll to the top of the messaging center container
        const container = document.querySelector('.messaging-center-container');
        if (container) {
          container.scrollTop = 0;
        }
        
        // Also scroll the window to the top for good measure
        window.scrollTo(0, 0);
      }
    };

    handleModalOpen();
  }, [showMessageModal, showComplaintModal, showViewModal, showReplyModal]);

  // ========== LOAD USER DATA ==========
  const loadUserData = useCallback(async () => {
    if (!user || !token) return;
    
    try {
      setLoadingUserData(true);
      
      // Use the unified messaging endpoint (more efficient)
      try {
        const response = await messagingApi.getMessagingUserData();
        
        if (response.data.success) {
          setUserOrders(response.data.orders || []);
          setUserPayments(response.data.payments || []);
        } else {
          // Fallback to separate endpoints
          await loadSeparateEndpoints();
        }
      } catch (err) {
        // Fallback to separate endpoints
        await loadSeparateEndpoints();
      }
      
    } catch (err) {
      console.error('Error loading user data:', err);
    } finally {
      setLoadingUserData(false);
    }
  }, [user, token]);

  // Helper function for fallback loading
  const loadSeparateEndpoints = useCallback(async () => {
    try {
      const [ordersResponse, paymentsResponse] = await Promise.allSettled([
        messagingApi.getUserOrders(),
        messagingApi.getUserPayments()
      ]);
      
      // Handle orders response
      if (ordersResponse.status === 'fulfilled' && ordersResponse.value.data) {
        const ordersData = Array.isArray(ordersResponse.value.data) 
          ? ordersResponse.value.data 
          : (ordersResponse.value.data.orders || []);
        setUserOrders(ordersData);
      } else {
        setUserOrders([]);
      }
      
      // Handle payments response
      if (paymentsResponse.status === 'fulfilled' && paymentsResponse.value.data) {
        const paymentsData = Array.isArray(paymentsResponse.value.data) 
          ? paymentsResponse.value.data 
          : (paymentsResponse.value.data.payments || []);
        setUserPayments(paymentsData);
      } else {
        setUserPayments([]);
      }
      
    } catch (fallbackErr) {
      console.error('Fallback loading failed:', fallbackErr);
      setUserOrders([]);
      setUserPayments([]);
    }
  }, []);

  // ========== LOAD MESSAGES ==========
  const loadMessages = useCallback(async () => {
    try {
      setError('');
      setLoading(true);
      
      // Check authentication
      if (!user || !token) {
        setError('Please log in to access messages');
        setMessages([]);
        return;
      }
      
      // Build query params
      const params = {};
      if (filters.message_type) params.message_type = filters.message_type;
      if (filters.is_read !== '') params.is_read = filters.is_read;
      if (filters.complaint_type) params.complaint_type = filters.complaint_type;
      if (filters.search) params.search = filters.search;
      params.ordering = sortDirection === 'desc' ? `-${sortField}` : sortField;
      
      const response = await messagingApi.getMessages(params);
      
      let messagesData = [];
      if (response.data && response.data.results) {
        messagesData = response.data.results;
      } else if (Array.isArray(response.data)) {
        messagesData = response.data;
      } else if (response.data && typeof response.data === 'object') {
        // Try to extract from object
        if (response.data.messages) {
          messagesData = response.data.messages;
        } else if (response.data.data) {
          messagesData = response.data.data;
        } else {
          // Check if it's a single message object
          const keys = Object.keys(response.data);
          if (keys.includes('id') && keys.includes('message_type')) {
            messagesData = [response.data];
          }
        }
      }
      
      setMessages(messagesData);
      
    } catch (err) {
      console.error('Error loading messages:', err);
      setError('Failed to load messages: ' + (err.response?.data?.detail || err.message));
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [filters, sortField, sortDirection, user, token]);

  // ========== LOAD INITIAL DATA ==========
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Check authentication
      if (!user || !token) {
        setError('You must be logged in to access messages');
        return;
      }
      
      // Load user data (orders and payments)
      await loadUserData();
      
      // Load messages
      await loadMessages();
      
      // Load stats
      try {
        const statsResponse = await messagingApi.getMessageStats();
        setStats(statsResponse.data);
      } catch (err) {
        console.error('Failed to load stats:', err);
      }
      
      // Load unread count
      try {
        const countResponse = await messagingApi.getUnreadCount();
        setUnreadCount(countResponse.data.total_unread || 0);
      } catch (err) {
        console.error('Failed to load unread count:', err);
      }
      
    } catch (err) {
      console.error('Initial load error:', err);
      setError('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [user, token, loadMessages, loadUserData]);

  // Load initial data on component mount
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Load messages when filters change
  useEffect(() => {
    if (user && token) {
      loadMessages();
    }
  }, [filters, loadMessages, user, token]);

  // ========== MESSAGE ACTIONS ==========
  const handleMarkAsRead = async (messageId) => {
    try {
      await messagingApi.markAsRead(messageId);
      
      setSuccess('Message marked as read');
      
      loadMessages();
      loadInitialData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to mark as read:', err);
      setError('Failed to mark message as read');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await messagingApi.markAllAsRead();
      setSuccess('All messages marked as read');
      
      loadMessages();
      loadInitialData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
      setError('Failed to mark all messages as read');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      try {
        await messagingApi.deleteMessage(messageId);
        setSuccess('Message deleted');
        
        loadMessages();
        if (currentMessage?.id === messageId) {
          setShowViewModal(false);
          setCurrentMessage(null);
        }
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        console.error('Failed to delete:', err);
        setError('Failed to delete message');
      }
    }
  };

  const loadMessageDetails = async (messageId) => {
    try {
      const response = await messagingApi.getMessage(messageId);
      setCurrentMessage(response.data);
      return response.data;
    } catch (err) {
      console.error('Error loading message details:', err);
      throw err;
    }
  };

  // ========== FILTER HANDLERS ==========
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    loadMessages();
  };

  const toggleSelectMessage = (messageId) => {
    setSelectedMessages(prev => 
      prev.includes(messageId) 
        ? prev.filter(id => id !== messageId)
        : [...prev, messageId]
    );
  };

  // ========== MODAL HANDLERS WITH SCROLL TO TOP ==========
  const handleNewMessage = () => {
    setNewMessageForm({
      messageType: 'CLIENT_MESSAGE',
      recipientId: user?.is_staff ? '' : 'admin',
      orderId: '',
      paymentId: '',
      subject: '',
      content: '',
      complaintType: '',
      attachment: null
    });
    setShowMessageModal(true);
    
    // Scroll to top when opening modal
    setTimeout(() => {
      const modalContainer = document.querySelector('.messaging-center-container');
      if (modalContainer) {
        modalContainer.scrollTop = 0;
      }
      window.scrollTo(0, 0);
    }, 10);
  };

  const handleNewComplaint = () => {
    setComplaintForm({
      complaintType: '',
      orderId: '',
      transactionId: '',
      subject: '',
      content: '',
      attachment: null,
    });
    setShowComplaintModal(true);
    
    // Scroll to top when opening modal
    setTimeout(() => {
      const modalContainer = document.querySelector('.messaging-center-container');
      if (modalContainer) {
        modalContainer.scrollTop = 0;
      }
      window.scrollTo(0, 0);
    }, 10);
  };

  const handleViewMessage = async (message) => {
    try {
      const detailedMessage = await loadMessageDetails(message.id);
      setCurrentMessage(detailedMessage);
      setShowViewModal(true);
      
      // Scroll to top when opening modal
      setTimeout(() => {
        const modalContainer = document.querySelector('.messaging-center-container');
        if (modalContainer) {
          modalContainer.scrollTop = 0;
        }
        window.scrollTo(0, 0);
      }, 10);
    } catch (err) {
      setError('Failed to load message details');
    }
  };

  const handleReplyToMessage = (message) => {
    setCurrentMessage(message);
    setReplyForm({
      content: '',
      attachment: null
    });
    setShowReplyModal(true);
    
    // Scroll to top when opening modal
    setTimeout(() => {
      const modalContainer = document.querySelector('.messaging-center-container');
      if (modalContainer) {
        modalContainer.scrollTop = 0;
      }
      window.scrollTo(0, 0);
    }, 10);
  };

  // ========== FORM FIELD HANDLERS ==========
  const handleComplaintTypeChange = (complaintType) => {
    setComplaintForm(prev => ({
      ...prev,
      complaintType,
      orderId: '', // Reset order ID when complaint type changes
      transactionId: '' // Reset transaction ID when complaint type changes
    }));
  };

  // ========== SEND MESSAGES ==========
  const handleSendMessage = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    
    if (!newMessageForm.subject.trim()) {
      setError('Subject is required');
      setSending(false);
      return;
    }
    
    if (!newMessageForm.content.trim()) {
      setError('Message content is required');
      setSending(false);
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('message_type', newMessageForm.messageType);
      formData.append('subject', newMessageForm.subject);
      formData.append('content', newMessageForm.content);
      
      if (newMessageForm.orderId) formData.append('order', newMessageForm.orderId);
      if (newMessageForm.paymentId) formData.append('payment', newMessageForm.paymentId);
      if (newMessageForm.complaintType) formData.append('complaint_type', newMessageForm.complaintType);
      if (newMessageForm.attachment) formData.append('attachment', newMessageForm.attachment);
      
      await messagingApi.createMessage(formData);
      
      setSuccess('Message sent successfully!');
      setShowMessageModal(false);
      setNewMessageForm({
        messageType: 'CLIENT_MESSAGE',
        recipientId: '',
        orderId: '',
        paymentId: '',
        subject: '',
        content: '',
        complaintType: '',
        attachment: null
      });
      
      loadMessages();
      loadInitialData();
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      console.error('Send message error:', err);
      setError('Failed to send message: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSending(false);
    }
  };

  const handleSubmitComplaint = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    
    if (!complaintForm.complaintType) {
      setError('Please select a complaint type');
      setSending(false);
      return;
    }
    
    if (!complaintForm.subject.trim()) {
      setError('Subject is required');
      setSending(false);
      return;
    }
    
    if (!complaintForm.content.trim()) {
      setError('Please describe your complaint');
      setSending(false);
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('message_type', 'COMPLAINT');
      formData.append('complaint_type', complaintForm.complaintType);
      formData.append('subject', complaintForm.subject);
      formData.append('content', complaintForm.content);
      
      if (complaintForm.orderId) formData.append('order', complaintForm.orderId);
      if (complaintForm.transactionId) formData.append('payment', complaintForm.transactionId);
      if (complaintForm.attachment) formData.append('attachment', complaintForm.attachment);
      
      await messagingApi.createMessage(formData);
      
      setSuccess('Complaint submitted successfully!');
      setShowComplaintModal(false);
      setComplaintForm({
        complaintType: '',
        orderId: '',
        transactionId: '',
        subject: '',
        content: '',
        attachment: null,
      });
      
      loadMessages();
      loadInitialData();
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      console.error('Submit complaint error:', err);
      setError('Failed to submit complaint: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSending(false);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!currentMessage) return;
    
    if (!replyForm.content.trim()) {
      setError('Reply content is required');
      return;
    }
    
    setSending(true);
    setError('');
    
    try {
      const formData = new FormData();
      formData.append('content', replyForm.content);
      
      if (replyForm.attachment) formData.append('attachment', replyForm.attachment);
      
      await messagingApi.replyToMessage(currentMessage.id, formData);
      
      setSuccess('Reply sent successfully!');
      setShowReplyModal(false);
      setReplyForm({
        content: '',
        attachment: null
      });
      
      loadMessages();
      loadInitialData();
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      console.error('Send reply error:', err);
      setError('Failed to send reply: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSending(false);
    }
  };

  // ========== FILE HANDLERS ==========
  const handleFileChange = (e, formType, field) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }
    
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/gif',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      setError('File type not supported. Please upload images, PDFs, or documents.');
      return;
    }
    
    if (formType === 'newMessage') {
      setNewMessageForm(prev => ({ ...prev, [field]: file }));
    } else if (formType === 'complaint') {
      setComplaintForm(prev => ({ ...prev, [field]: file }));
    } else if (formType === 'reply') {
      setReplyForm(prev => ({ ...prev, [field]: file }));
    }
    
    setError('');
  };

  const handleRemoveFile = (formType) => {
    if (formType === 'newMessage') {
      setNewMessageForm(prev => ({ ...prev, attachment: null }));
    } else if (formType === 'complaint') {
      setComplaintForm(prev => ({ ...prev, attachment: null }));
    } else if (formType === 'reply') {
      setReplyForm(prev => ({ ...prev, attachment: null }));
    }
  };

  // ========== UTILITY FUNCTIONS ==========
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMessageTypeInfo = (type) => {
    return messageTypeLabels[type] || { label: type, color: 'gray', icon: <FaEnvelope /> };
  };

  const handleDownloadAttachment = (attachmentUrl, attachmentName) => {
    if (attachmentUrl) {
      const link = document.createElement('a');
      link.href = attachmentUrl;
      link.download = attachmentName || 'attachment';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // ========== CONDITIONAL FIELD RENDERS ==========
  const renderOrderField = () => {
    const showOrderField = ['ORDER_QUALITY', 'DELIVERY_TIME', 'WRITER_PERFORMANCE'].includes(complaintForm.complaintType);
    
    if (!showOrderField) return null;
    
    return (
      <div className="form-group">
        <label>Order *</label>
        <select
          value={complaintForm.orderId}
          onChange={(e) => setComplaintForm(prev => ({ ...prev, orderId: e.target.value }))}
          className="form-select"
          required
        >
          <option value="">Select Order</option>
          {userOrders.map(order => (
            <option key={order.id} value={order.id}>
              {order.order_number || `ORD-${order.id}`} - {order.title || 'Untitled Order'} 
              {order.status && ` (${order.status})`}
            </option>
          ))}
        </select>
        {userOrders.length === 0 && (
          <p className="field-hint">
            <FaBox /> No orders found for your account.
          </p>
        )}
      </div>
    );
  };

  const renderPaymentFields = () => {
    if (complaintForm.complaintType !== 'PAYMENT_ISSUE') return null;
    
    return (
      <>
        <div className="form-group">
          <label>Order (Optional)</label>
          <select
            value={complaintForm.orderId}
            onChange={(e) => setComplaintForm(prev => ({ ...prev, orderId: e.target.value }))}
            className="form-select"
          >
            <option value="">Select Order (Optional)</option>
            {userOrders.map(order => (
              <option key={order.id} value={order.id}>
                {order.order_number || `ORD-${order.id}`} - {order.title || 'Untitled Order'}
              </option>
            ))}
          </select>
        </div>
        
        <div className="form-group">
          <label>Transaction ID (Optional)</label>
          <select
            value={complaintForm.transactionId}
            onChange={(e) => setComplaintForm(prev => ({ ...prev, transactionId: e.target.value }))}
            className="form-select"
          >
            <option value="">Select Transaction (Optional)</option>
            {userPayments.map(payment => (
              <option key={payment.id} value={payment.id}>
                {payment.transaction_number || `TXN-${payment.transaction_id || payment.id}`} - 
                ${payment.amount || payment.formatted_amount || '0.00'}
                {payment.status && ` (${payment.status})`}
              </option>
            ))}
          </select>
          {userPayments.length === 0 && (
            <p className="field-hint">
              <FaReceipt /> No payment transactions found for your account.
            </p>
          )}
        </div>
      </>
    );
  };

  const renderMessageRow = (message) => {
    const typeInfo = getMessageTypeInfo(message.message_type);
    const isSelected = selectedMessages.includes(message.id);
    const isFromCurrentUser = message.sender?.id === user?.id || message.sender_details?.id === user?.id;
    
    return (
      <tr 
        key={message.id} 
        className={`message-row ${message.is_read ? 'read' : 'unread'} ${isSelected ? 'selected' : ''}`}
        onClick={() => handleViewMessage(message)}
      >
        <td onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => toggleSelectMessage(message.id)}
            onClick={(e) => e.stopPropagation()}
          />
        </td>
        <td>
          <div className="message-type-cell">
            <span className={`type-badge type-${typeInfo.color}`}>
              {typeInfo.icon}
              <span className="type-label">{typeInfo.label}</span>
            </span>
            {message.attachment && <FaPaperclip className="attachment-indicator" />}
            {!message.is_read && <span className="unread-indicator"></span>}
          </div>
        </td>
        <td className="message-subject-cell">
          <div className="message-subject">
            {message.subject || 'No Subject'}
            {message.complaint_type && (
              <span className="complaint-tag">
                <FaTag /> {complaintTypeLabels[message.complaint_type] || message.complaint_type}
              </span>
            )}
          </div>
        </td>
        <td className="message-content-cell">
          <div className="message-preview">
            {message.content?.substring(0, 100)}
            {message.content?.length > 100 && '...'}
          </div>
        </td>
        <td>
          <div className="user-cell">
            <FaUser className="user-icon" />
            <span className="user-name">
              {message.sender_details?.username || message.sender?.username || 'Unknown'}
              {isFromCurrentUser && <span className="you-badge">(You)</span>}
            </span>
          </div>
        </td>
        <td>
          <div className="user-cell">
            <FaUser className="user-icon" />
            <span className="user-name">
              {message.recipient_details?.username || message.recipient?.username || 'Unknown'}
              {message.recipient?.id === user?.id && <span className="you-badge">(You)</span>}
            </span>
          </div>
        </td>
        <td>
          <div className="date-cell">
            <FaClock className="date-icon" />
            <span className="date-text">{formatDate(message.created_at)}</span>
          </div>
        </td>
        <td onClick={(e) => e.stopPropagation()}>
          <div className="action-buttons">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleViewMessage(message);
              }}
              className="btn-action view"
              title="View"
            >
              <FaEye />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleReplyToMessage(message);
              }}
              className="btn-action reply"
              title="Reply"
            >
              <FaReply />
            </button>
            {!message.is_read && message.recipient?.id === user?.id && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleMarkAsRead(message.id);
                }}
                className="btn-action mark-read"
                title="Mark as Read"
              >
                <FaEnvelopeOpen />
              </button>
            )}
            {isFromCurrentUser && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteMessage(message.id);
                }}
                className="btn-action delete"
                title="Delete"
              >
                <FaTrash />
            </button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  // ========== MODAL RENDER FUNCTIONS ==========
  const renderViewModal = () => {
    if (!currentMessage) return null;
    
    const typeInfo = getMessageTypeInfo(currentMessage.message_type);
    const isOwner = currentMessage.sender?.id === user?.id || currentMessage.sender_details?.id === user?.id;
    const isRecipient = currentMessage.recipient?.id === user?.id;
    
    return (
      <div className={`modal ${showViewModal ? 'show' : ''}`}>
        <div className="modal-content message-view-modal">
          <div className="modal-header">
            <h2>{currentMessage.subject || 'No Subject'}</h2>
            <button className="modal-close" onClick={() => setShowViewModal(false)}>
              <FaTimes />
            </button>
          </div>
          
          <div className="modal-body">
            <div className="message-view-header">
              <div className="message-type">
                <span className={`type-badge type-${typeInfo.color}`}>
                  {typeInfo.icon}
                  <span className="type-label">{typeInfo.label}</span>
                </span>
                
                {currentMessage.complaint_type && (
                  <span className="complaint-badge">
                    <FaTag /> {complaintTypeLabels[currentMessage.complaint_type] || currentMessage.complaint_type}
                  </span>
                )}
                
                {currentMessage.complaint_status && (
                  <span className={`status-badge status-${currentMessage.complaint_status.toLowerCase()}`}>
                    {complaintStatusLabels[currentMessage.complaint_status] || currentMessage.complaint_status}
                  </span>
                )}
                
                {!currentMessage.is_read && isRecipient && (
                  <span className="unread-badge">UNREAD</span>
                )}
              </div>
              
              <div className="message-date">
                <FaClock className="date-icon" />
                <span className="date-text">{formatDate(currentMessage.created_at)}</span>
              </div>
            </div>
            
            <div className="message-participants">
              <div className="participant sender">
                <div className="participant-label">From:</div>
                <div className="participant-info">
                  <FaUser className="participant-icon" />
                  <div className="participant-details">
                    <div className="participant-name">
                      {currentMessage.sender_details?.username || currentMessage.sender?.username || 'Unknown'}
                    </div>
                    <div className="participant-email">
                      {currentMessage.sender_details?.email || currentMessage.sender?.email || ''}
                    </div>
                  </div>
                  {isOwner && <span className="you-label">(You)</span>}
                </div>
              </div>
              
              <div className="participant recipient">
                <div className="participant-label">To:</div>
                <div className="participant-info">
                  <FaUser className="participant-icon" />
                  <div className="participant-details">
                    <div className="participant-name">
                      {currentMessage.recipient_details?.username || currentMessage.recipient?.username || 'Unknown'}
                    </div>
                    <div className="participant-email">
                      {currentMessage.recipient_details?.email || currentMessage.recipient?.email || ''}
                    </div>
                  </div>
                  {isRecipient && <span className="you-label">(You)</span>}
                </div>
              </div>
            </div>
            
            <div className="message-content">
              <div className="content-label">Message:</div>
              <div className="content-text">{currentMessage.content}</div>
            </div>
            
            {currentMessage.attachment && (
              <div className="message-attachment">
                <div className="attachment-label">
                  <FaPaperclip className="attachment-icon" />
                  Attachment:
                </div>
                <div className="attachment-info">
                  <span className="attachment-name">
                    {currentMessage.attachment_name || 'Attachment'}
                  </span>
                  <button 
                    onClick={() => handleDownloadAttachment(currentMessage.attachment_url, currentMessage.attachment_name)}
                    className="btn-download"
                  >
                    <FaDownload /> Download
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <div className="modal-footer">
            <div className="footer-actions">
              {isRecipient && !currentMessage.is_read && (
                <button
                  onClick={() => handleMarkAsRead(currentMessage.id)}
                  className="btn-mark-read"
                >
                  <FaEnvelopeOpen /> Mark as Read
                </button>
              )}
              <button
                onClick={() => handleReplyToMessage(currentMessage)}
                className="btn-reply"
              >
                <FaReply /> Reply
              </button>
              <button
                onClick={() => window.print()}
                className="btn-print"
              >
                <FaPrint /> Print
              </button>
              {isOwner && (
                <button
                  onClick={() => handleDeleteMessage(currentMessage.id)}
                  className="btn-delete"
                >
                  <FaTrash /> Delete
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderNewMessageModal = () => (
    <div className={`modal ${showMessageModal ? 'show' : ''}`}>
      <div className="modal-content new-message-modal">
        <div className="modal-header">
          <h2>New Message</h2>
          <button className="modal-close" onClick={() => setShowMessageModal(false)}>
            <FaTimes />
          </button>
        </div>
        
        <div className="modal-body">
          <form onSubmit={handleSendMessage}>
            <div className="form-group">
              <label>Message Type *</label>
              <select
                value={newMessageForm.messageType}
                onChange={(e) => setNewMessageForm({...newMessageForm, messageType: e.target.value})}
                className="form-select"
              >
                <option value="CLIENT_MESSAGE">Message to Admin</option>
                <option value="SUPPORT_TICKET">Support Ticket</option>
              </select>
            </div>
            
            <div className="form-group">
              <label>Subject *</label>
              <input
                type="text"
                value={newMessageForm.subject}
                onChange={(e) => setNewMessageForm({...newMessageForm, subject: e.target.value})}
                className="form-input"
                placeholder="Enter message subject..."
                required
              />
            </div>
            
            <div className="form-group">
              <label>Message Content *</label>
              <textarea
                value={newMessageForm.content}
                onChange={(e) => setNewMessageForm({...newMessageForm, content: e.target.value})}
                className="form-textarea"
                rows="6"
                placeholder="Type your message here..."
                required
              />
            </div>
            
            <div className="form-group">
              <label className="file-label">
                <FaPaperclip className="file-icon" />
                Attachment (Optional)
              </label>
              <input
                type="file"
                id="newMessageAttachment"
                onChange={(e) => handleFileChange(e, 'newMessage', 'attachment')}
                className="file-input"
                accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.txt"
              />
              
              {newMessageForm.attachment && (
                <div className="file-preview">
                  <div className="file-info">
                    <FaPaperclip className="preview-icon" />
                    <div className="file-details">
                      <div className="file-name">{newMessageForm.attachment.name}</div>
                      <div className="file-size">
                        {(newMessageForm.attachment.size / 1024 / 1024).toFixed(2)} MB
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-remove-file"
                    onClick={() => handleRemoveFile('newMessage')}
                  >
                    <FaTimes />
                  </button>
                </div>
              )}
              
              <p className="file-hint">
                Max file size: 10MB. Supported: Images, PDF, Word, Text
              </p>
            </div>
            
            <div className="form-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setShowMessageModal(false)}
                disabled={sending}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-submit"
                disabled={sending}
              >
                {sending ? (
                  <>
                    <FaSpinner className="spinner-icon" />
                    Sending...
                  </>
                ) : (
                  'Send Message'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  const renderComplaintModal = () => (
    <div className={`modal ${showComplaintModal ? 'show' : ''}`}>
      <div className="modal-content new-complaint-modal">
        <div className="modal-header">
          <h2><FaExclamationTriangle /> Submit Complaint</h2>
          <button className="modal-close" onClick={() => setShowComplaintModal(false)}>
            <FaTimes />
          </button>
        </div>
        
        <div className="modal-body">
          <form onSubmit={handleSubmitComplaint}>
            <div className="form-group">
              <label>Complaint Type *</label>
              <select
                value={complaintForm.complaintType}
                onChange={(e) => handleComplaintTypeChange(e.target.value)}
                className="form-select"
                required
              >
                <option value="">Select complaint type...</option>
                <option value="ORDER_QUALITY">Order Quality</option>
                <option value="PAYMENT_ISSUE">Payment Issue</option>
                <option value="DELIVERY_TIME">Delivery Time</option>
                <option value="WRITER_PERFORMANCE">Writer Performance</option>
                <option value="GENERAL">General</option>
                <option value="TECHNICAL">Technical</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            
            {renderOrderField()}
            {renderPaymentFields()}
            
            <div className="form-group">
              <label>Subject *</label>
              <input
                type="text"
                value={complaintForm.subject}
                onChange={(e) => setComplaintForm({...complaintForm, subject: e.target.value})}
                className="form-input"
                placeholder="Enter complaint subject..."
                required
              />
            </div>
            
            <div className="form-group">
              <label>Complaint Details *</label>
              <textarea
                value={complaintForm.content}
                onChange={(e) => setComplaintForm({...complaintForm, content: e.target.value})}
                className="form-textarea"
                rows="6"
                placeholder="Please describe your complaint in detail..."
                required
              />
            </div>
            
            <div className="form-group">
              <label className="file-label">
                <FaPaperclip className="file-icon" />
                Supporting Documents (Optional)
              </label>
              <input
                type="file"
                id="complaintAttachment"
                onChange={(e) => handleFileChange(e, 'complaint', 'attachment')}
                className="file-input"
                accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.txt"
              />
              
              {complaintForm.attachment && (
                <div className="file-preview">
                  <div className="file-info">
                    <FaPaperclip className="preview-icon" />
                    <div className="file-details">
                      <div className="file-name">{complaintForm.attachment.name}</div>
                      <div className="file-size">
                        {(complaintForm.attachment.size / 1024 / 1024).toFixed(2)} MB
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-remove-file"
                    onClick={() => handleRemoveFile('complaint')}
                  >
                    <FaTimes />
                  </button>
                </div>
              )}
              
              <p className="file-hint">
                Max file size: 10MB. You can attach screenshots, documents, or other supporting evidence.
              </p>
            </div>
            
            <div className="form-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setShowComplaintModal(false)}
                disabled={sending}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-submit"
                disabled={sending}
              >
                {sending ? (
                  <>
                    <FaSpinner className="spinner-icon" />
                    Submitting...
                  </>
                ) : (
                  'Submit Complaint'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  const renderReplyModal = () => (
    <div className={`modal ${showReplyModal ? 'show' : ''}`}>
      <div className="modal-content reply-modal">
        <div className="modal-header">
          <h2><FaReply /> Reply to Message</h2>
          <button className="modal-close" onClick={() => setShowReplyModal(false)}>
            <FaTimes />
          </button>
        </div>
        
        <div className="modal-body">
          <form onSubmit={handleSendReply}>
            <div className="form-group">
              <label>Reply Content *</label>
              <textarea
                value={replyForm.content}
                onChange={(e) => setReplyForm({...replyForm, content: e.target.value})}
                className="form-textarea"
                rows="6"
                placeholder="Type your reply here..."
                required
              />
            </div>
            
            <div className="form-group">
              <label className="file-label">
                <FaPaperclip className="file-icon" />
                Attachment (Optional)
              </label>
              <input
                type="file"
                id="replyAttachment"
                onChange={(e) => handleFileChange(e, 'reply', 'attachment')}
                className="file-input"
                accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.txt"
              />
              
              {replyForm.attachment && (
                <div className="file-preview">
                  <div className="file-info">
                    <FaPaperclip className="preview-icon" />
                    <div className="file-details">
                      <div className="file-name">{replyForm.attachment.name}</div>
                      <div className="file-size">
                        {(replyForm.attachment.size / 1024 / 1024).toFixed(2)} MB
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-remove-file"
                    onClick={() => handleRemoveFile('reply')}
                  >
                    <FaTimes />
                  </button>
                </div>
              )}
              
              <p className="file-hint">
                Max file size: 10MB. Supported: Images, PDF, Word, Text
              </p>
            </div>
            
            <div className="form-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setShowReplyModal(false)}
                disabled={sending}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-submit"
                disabled={sending}
              >
                {sending ? (
                  <>
                    <FaSpinner className="spinner-icon" />
                    Sending...
                  </>
                ) : (
                  <>
                    <FaReply /> Send Reply
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  // ========== MAIN RENDER ==========
  if (loading) {
    return (
      <div className="messaging-center-loading">
        <div className="loading-spinner"></div>
        <p>Loading messages...</p>
      </div>
    );
  }

  return (
    <div className="messaging-center-container">
      {/* Header */}
      <div className="messaging-center-header">
        <div className="header-left">
          <h1>
            Messages
            {unreadCount > 0 && (
              <span className="unread-badge">{unreadCount} unread</span>
            )}
          </h1>
          <p className="header-subtitle">
            {messages.length} messages • {unreadCount} unread
            {stats && stats.stats && (
              <span className="stats-summary">
                • {stats.stats.complaints || 0} complaints
              </span>
            )}
          </p>
        </div>
        
        <div className="header-right">
          <button
            onClick={loadInitialData}
            className="btn-refresh-all"
            title="Refresh All Data"
          >
            <FaSync /> Refresh
          </button>
          <button
            onClick={handleMarkAllAsRead}
            className="btn-mark-all-read"
            disabled={unreadCount === 0}
          >
            <FaEnvelopeOpen /> Mark All Read
          </button>
          <button
            onClick={handleNewMessage}
            className="btn-new-message"
          >
            <FaPlus /> New Message
          </button>
          <button
            onClick={handleNewComplaint}
            className="btn-new-complaint"
          >
            <FaExclamationTriangle /> New Complaint
          </button>
        </div>
      </div>
      
      {/* Alerts */}
      {error && (
        <div className="alert alert-error">
          <FaExclamationTriangle /> {error}
          <button onClick={() => setError('')} className="alert-close">
            <FaTimes />
          </button>
        </div>
      )}
      
      {success && (
        <div className="alert alert-success">
          <FaCheck /> {success}
          <button onClick={() => setSuccess('')} className="alert-close">
            <FaTimes />
          </button>
        </div>
      )}
      
      {/* Filters */}
      <div className="message-filters">
        <div className="filter-group">
          <div className="filter-input">
            <FaSearch className="filter-icon" />
            <input
              type="text"
              placeholder="Search messages..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="search-input"
            />
          </div>
        </div>
        
        <div className="filter-group">
          <select
            value={filters.message_type}
            onChange={(e) => handleFilterChange('message_type', e.target.value)}
            className="filter-select"
          >
            <option value="">All Types</option>
            {Object.entries(messageTypeLabels).map(([key, info]) => (
              <option key={key} value={key}>{info.label}</option>
            ))}
          </select>
          
          <select
            value={filters.is_read}
            onChange={(e) => handleFilterChange('is_read', e.target.value)}
            className="filter-select"
          >
            <option value="">All Status</option>
            <option value="false">Unread Only</option>
            <option value="true">Read Only</option>
          </select>
          
          <select
            value={filters.complaint_type}
            onChange={(e) => handleFilterChange('complaint_type', e.target.value)}
            className="filter-select"
          >
            <option value="">All Complaint Types</option>
            {Object.entries(complaintTypeLabels).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Messages Table */}
      <div className="messages-table-container">
        <table className="messages-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}>
                <input
                  type="checkbox"
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedMessages(messages.map(msg => msg.id));
                    } else {
                      setSelectedMessages([]);
                    }
                  }}
                  checked={selectedMessages.length === messages.length && messages.length > 0}
                />
              </th>
              <th onClick={() => handleSort('message_type')} className="sortable">
                Message Type {sortField === 'message_type' && (
                  sortDirection === 'desc' ? <FaCaretDown /> : <FaCaretUp />
                )}
              </th>
              <th>Subject</th>
              <th>Message Content</th>
              <th onClick={() => handleSort('sender__username')} className="sortable">
                From {sortField === 'sender__username' && (
                  sortDirection === 'desc' ? <FaCaretDown /> : <FaCaretUp />
                )}
              </th>
              <th>To</th>
              <th onClick={() => handleSort('created_at')} className="sortable">
                Date {sortField === 'created_at' && (
                  sortDirection === 'desc' ? <FaCaretDown /> : <FaCaretUp />
                )}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {messages.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-messages">
                  <div className="empty-state">
                    <FaEnvelope className="empty-icon" />
                    <p>No messages found</p>
                    <div className="empty-actions">
                      <button
                        onClick={handleNewMessage}
                        className="btn-empty-action"
                      >
                        <FaPlus /> Send your first message
                      </button>
                      <button
                        onClick={handleNewComplaint}
                        className="btn-empty-action"
                      >
                        <FaExclamationTriangle /> Submit a complaint
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              messages.map(renderMessageRow)
            )}
          </tbody>
        </table>
      </div>
      
      {/* Selected Messages Actions */}
      {selectedMessages.length > 0 && (
        <div className="selected-actions-bar">
          <div className="selected-count">
            {selectedMessages.length} message{selectedMessages.length !== 1 ? 's' : ''} selected
          </div>
          <div className="selected-buttons">
            <button
              onClick={() => {
                selectedMessages.forEach(id => handleMarkAsRead(id));
                setSelectedMessages([]);
              }}
              className="btn-selected-action"
            >
              <FaEnvelopeOpen /> Mark as Read
            </button>
            <button
              onClick={() => {
                if (window.confirm(`Delete ${selectedMessages.length} message(s)?`)) {
                  selectedMessages.forEach(id => handleDeleteMessage(id));
                  setSelectedMessages([]);
                }
              }}
              className="btn-selected-action delete"
            >
              <FaTrash /> Delete
            </button>
            <button
              onClick={() => setSelectedMessages([])}
              className="btn-selected-action cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {/* Modals */}
      {renderViewModal()}
      {renderNewMessageModal()}
      {renderComplaintModal()}
      {renderReplyModal()}
    </div>
  );
};

export default MessagingCenter;